import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAdminEvent } from "@/context/AppContext";
import { api } from "@/lib/api";
import { PageIntro, StatCard, Card } from "@/admin/ui";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Search, Plane, PlaneTakeoff, PlaneLanding, Users, CircleCheck, Clock,
  TriangleAlert, HelpCircle, History, Lock, ChevronRight,
  FileSpreadsheet, X, ShieldCheck, RefreshCw, Upload, Trash2, FileText, Plus,
} from "lucide-react";

const STATUS_STYLES = {
  "Complete · Navan": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Complete · Outside Invite": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Complete · Outside Navan": "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  "Complete · Return self-arranged": "bg-[hsl(var(--secondary)/0.15)] text-[hsl(var(--secondary))]",
  "Booking Pending": "bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(38_50%_34%)]",
  "Genuine Partial": "bg-[hsl(38_80%_90%)] text-[hsl(38_50%_35%)]",
  "Needs Review": "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]",
  "NA / No Flight Required": "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  "Source Status Missing / Needs Confirmation": "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]",
};
const FARE_STYLES = {
  Captured: "bg-[hsl(var(--mint))] text-[hsl(var(--primary))]",
  Missing: "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]",
  "Booking Pending": "bg-[hsl(var(--pc-gold)/0.16)] text-[hsl(38_50%_34%)]",
  "Not Applicable": "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]",
  "Unknown / Awaiting Source Confirmation": "bg-[hsl(0_70%_92%)] text-[hsl(0_60%_42%)]",
};

const FILTERS = [
  ["All", () => true],
  ["Complete", (r) => r.travel_status.startsWith("Complete")],
  ["Booking Pending", (r) => r.travel_status === "Booking Pending"],
  ["Needs Review", (r) => r.travel_status === "Needs Review"],
  ["Source Confirmation Needed", (r) => r.travel_status === "Source Status Missing / Needs Confirmation"],
  ["Navan Invite", (r) => r.flight_source === "Navan Invite"],
  ["Outside Invite", (r) => r.flight_source === "Booked Outside Invite"],
  ["Outside Navan", (r) => r.flight_source === "Booked Outside Navan"],
  ["Self-arranged", (r) => r.travel_status === "Complete · Return self-arranged"],
  ["Fare Missing", (r) => r.fare_status === "Missing"],
];

const money = (v, cur) => (v == null ? "—" : `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${cur ? " " + cur : ""}`);
const dstr = (v) => (v == null ? "—" : String(v).slice(0, 10));

function Badge({ text, cls }) {
  return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${cls || "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]"}`}>{text}</span>;
}

export default function FlightReconciliation() {
  const { eventId } = useAdminEvent();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get("import") === "1") {
      setShowImport(true);
      const next = new URLSearchParams(searchParams);
      next.delete("import");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!eventId) return;
    setLoading(true); setErr(null);
    // Normal load reads the PERSISTED canonical state — the SAME helper Travel uses
    // (/travel/canonical) — so the workflow page can never disagree with Travel and
    // never rebuilds from stale source files / old import jobs. An uploaded batch
    // (Analyze) temporarily replaces `data` with the batch preview until Apply.
    api.travelCanonical(eventId)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load reconciliation"))
      .finally(() => setLoading(false));
  }, [eventId]);

  const rows = useMemo(() => {
    if (!data) return [];
    const f = FILTERS.find(([n]) => n === filter)?.[1] || (() => true);
    const term = q.trim().toLowerCase();
    return data.rows.filter(f).filter((r) =>
      !term || (r.attendee || "").toLowerCase().includes(term) || (r.email || "").toLowerCase().includes(term));
  }, [data, filter, q]);

  if (loading) return <div data-testid="flight-recon-loading" className="p-8 text-sm text-[hsl(var(--muted-foreground))]">Loading reconciliation…</div>;
  if (err) return <div data-testid="flight-recon-error" className="p-8 text-sm text-[hsl(0_60%_42%)]">{err}</div>;
  if (!data) return null;

  const k = data.kpis;

  return (
    <div data-testid="flight-reconciliation-page" className="space-y-6">
      <PageIntro>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-2xl text-[hsl(var(--primary))] flex items-center gap-2"><Plane className="h-6 w-6" /> Flight Manifest &amp; Reconciliation</h1>
            <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">Canonical multi-source reconciliation (PC Master + Navan manifests) for every active traveler. Manager-only. Airfare and source data are never exposed to attendees.</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="import-flight-sources-btn" onClick={() => setShowImport(true)} className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white px-4 py-2 text-sm font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--muted))]"><Upload className="h-4 w-4" /> Import Flight Sources</button>
            {data.apply_enabled && data.batch_signature ? (
              <>
                <span data-testid="apply-ready-note" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--mint))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--primary))]"><ShieldCheck className="h-3.5 w-3.5" /> Batch analyzed — review &amp; confirm to Apply</span>
                <button data-testid="apply-button" onClick={() => setShowApply(true)} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-[opacity]"><ShieldCheck className="h-4 w-4" /> Review &amp; Apply</button>
              </>
            ) : (
              <>
                <span data-testid="apply-disabled-note" className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--muted))] px-3 py-1.5 text-xs font-medium text-[hsl(var(--muted-foreground))]"><Lock className="h-3.5 w-3.5" /> {data.apply_note || "Upload Travel Master + Navan manifests to analyze a new batch."}</span>
                <button data-testid="apply-button" disabled title={data.apply_note} className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white opacity-40 cursor-not-allowed"><ShieldCheck className="h-4 w-4" /> Apply</button>
              </>
            )}
          </div>
        </div>
      </PageIntro>

      {data.files && (
        <Card data-testid="analyzed-sources-panel">
          <div className="flex items-center gap-2 mb-2">
            <FileSpreadsheet className="h-4 w-4 text-[hsl(var(--secondary))]" />
            <h3 className="font-semibold text-[hsl(var(--primary))]">Analyzed sources (uploaded batch)</h3>
            <span className="ml-auto text-[10px] uppercase tracking-wide rounded-full bg-[hsl(var(--mint))] text-[hsl(var(--primary))] px-2 py-0.5 font-semibold">Zero writes</span>
          </div>
          <div className="text-sm space-y-1">
            <div className="flex items-center justify-between"><span className="text-[hsl(var(--muted-foreground))]">Travel Master · sheet “{data.files.travel_master.sheet_detected}”</span><span className="font-medium text-[hsl(var(--primary))]">{data.files.travel_master.filename} · {data.files.travel_master.master_rows} rows</span></div>
            {data.files.navan_manifests.map((f) => (
              <div key={f.filename} className="flex items-center justify-between"><span className="text-[hsl(var(--muted-foreground))]">Navan manifest</span><span className="font-medium text-[hsl(var(--primary))]">{f.filename} · {f.rows} rows</span></div>
            ))}
          </div>
        </Card>
      )}

      {/* Travel KPIs */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2">Travel</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Active Travelers" value={k.travel.active} icon={Users} />
          <StatCard label="Complete" value={k.travel.complete} icon={CircleCheck} accent="mint" />
          <StatCard label="Booking Pending" value={k.travel.booking_pending} icon={Clock} />
          <StatCard label="Needs Review" value={k.travel.needs_review} icon={TriangleAlert} />
          <StatCard label="Source Confirmation Needed" value={k.travel.source_confirmation_needed} icon={HelpCircle} />
        </div>
      </div>

      {/* Booking source + Airfare */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">Booking Source</div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <SrcRow label="Navan Invite" v={k.source.navan_invite} />
            <SrcRow label="Outside Invite" v={k.source.outside_invite} />
            <SrcRow label="Outside Navan" v={k.source.outside_navan} />
            <SrcRow label="Pending" v={k.source.pending} />
            <SrcRow label="Unknown" v={k.source.unknown} />
          </div>
        </Card>
        <Card>
          <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3">Airfare Summary</div>
          {/* Primary KPI */}
          <div data-testid="airfare-total-captured">
            <div className="font-serif text-[2rem] leading-none text-[hsl(var(--primary))]">${money(k.total_captured_airfare?.amount ?? (k.managed_navan_airfare.amount + k.master_external_fares.amount), "USD")}</div>
            <div className="mt-1.5 text-sm font-semibold text-[hsl(var(--foreground))]">Total Captured Airfare</div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">{k.total_captured_airfare?.count ?? k.airfare.captured} fares captured</div>
          </div>
          {/* Breakdown */}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border border-[hsl(var(--border))] p-3">
              <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Navan</div>
              <div data-testid="managed-navan-airfare" className="mt-0.5 font-semibold text-[hsl(var(--primary))]">${money(k.managed_navan_airfare.amount)}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{k.managed_navan_airfare.count} fares</div>
            </div>
            <div className="rounded-xl border border-[hsl(var(--border))] p-3">
              <div className="text-[11px] uppercase tracking-wide text-[hsl(var(--muted-foreground))]">External Airfare</div>
              <div data-testid="external-airfare" className="mt-0.5 font-semibold text-[hsl(var(--primary))]">${money(k.master_external_fares.amount)}</div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">{k.master_external_fares.count} fares · USD</div>
            </div>
          </div>
          {/* Compact status row */}
          <div className="mt-3 flex flex-wrap gap-2 text-xs" data-testid="airfare-status-row">
            <span data-testid="airfare-missing" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--muted))] px-2.5 py-1 font-medium text-[hsl(var(--muted-foreground))]">{k.airfare.missing} Fare Missing</span>
            <span data-testid="airfare-pending" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--muted))] px-2.5 py-1 font-medium text-[hsl(var(--muted-foreground))]">{k.airfare.pending_booking} Booking Pending</span>
            <span data-testid="airfare-unconfirmed" className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--muted))] px-2.5 py-1 font-medium text-[hsl(var(--muted-foreground))]">{k.airfare.source_unconfirmed} Source Unconfirmed</span>
          </div>
        </Card>
      </div>

      {/* Historical fares */}
      {data.historical_fares?.length > 0 && (
        <Card>
          <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5"><History className="h-4 w-4" /> Historical / Superseded Fare Records <span className="normal-case font-normal">(excluded from current totals)</span></div>
          {data.historical_fares.map((h, i) => (
            <div key={i} data-testid="historical-fare-row" className="flex items-center justify-between text-sm py-1">
              <span>{h.attendee} <span className="text-[hsl(var(--muted-foreground))]">· {h.source}</span></span>
              <span className="inline-flex items-center gap-2"><span className="line-through text-[hsl(var(--muted-foreground))]">{money(h.amount, h.currency)}</span><Badge text="Superseded / Historical" cls="bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" /></span>
            </div>
          ))}
        </Card>
      )}

      {/* Filters + search */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
          <Input data-testid="flight-recon-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search attendee or email…" className="pl-9" />
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap">
        {FILTERS.map(([name]) => (
          <button key={name} data-testid={`filter-${name.replace(/\s+/g,'-').toLowerCase()}`} onClick={() => setFilter(name)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${filter === name ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted)/0.7)]"}`}>
            {name}
          </button>
        ))}
      </div>

      {/* Table */}
      <Card className="overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table data-testid="flight-recon-table" className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted)/0.5)] text-left text-xs uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              <tr>
                <th className="px-4 py-3">Attendee</th>
                <th className="px-3 py-3">RSVP</th>
                <th className="px-3 py-3">Flight Source</th>
                <th className="px-3 py-3">Travel Status</th>
                <th className="px-3 py-3">Arrival (SJD)</th>
                <th className="px-3 py-3">Departure (SJD)</th>
                <th className="px-3 py-3">Airfare</th>
                <th className="px-3 py-3">Fare</th>
                <th className="px-3 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[hsl(var(--border))]">
              {rows.map((r) => (
                <tr key={r.email} data-testid="flight-recon-row" className="hover:bg-[hsl(var(--muted)/0.4)] cursor-pointer" onClick={() => setSel(r)}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[hsl(var(--foreground))]">{r.attendee}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">{r.email}</div>
                  </td>
                  <td className="px-3 py-3 capitalize">{r.rsvp}</td>
                  <td className="px-3 py-3">{r.flight_source}</td>
                  <td className="px-3 py-3"><Badge text={r.travel_status} cls={STATUS_STYLES[r.travel_status]} /></td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">{r.arr_flight ? <span className="inline-flex items-center gap-1"><PlaneLanding className="h-3 w-3" /> {dstr(r.arr_date)} {r.arr_time ? String(r.arr_time).slice(0,5) : ""} · {r.arr_flight}</span> : "—"}</td>
                  <td className="px-3 py-3 whitespace-nowrap text-xs">{r.dep_flight ? <span className="inline-flex items-center gap-1"><PlaneTakeoff className="h-3 w-3" /> {dstr(r.dep_date)} · {r.dep_flight}</span> : (r.travel_status === "Complete · Return self-arranged" ? <span className="italic text-[hsl(var(--secondary))]">Self-arranged</span> : "—")}</td>
                  <td className="px-3 py-3 whitespace-nowrap">{money(r.airfare, r.currency)}</td>
                  <td className="px-3 py-3"><Badge text={r.fare_status} cls={FARE_STYLES[r.fare_status]} /></td>
                  <td className="px-3 py-3 text-right"><ChevronRight className="h-4 w-4 text-[hsl(var(--muted-foreground))]" /></td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-[hsl(var(--muted-foreground))]">No travelers match this filter.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Apply preview (only while an uploaded batch is being reviewed) */}
      {data.apply_preview && (
      <Card>
        <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-3 flex items-center gap-1.5"><RefreshCw className="h-4 w-4" /> Proposed Apply (preview only — nothing is written)</div>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5 text-sm">
          <AP label="Create / update canonical itineraries" v={data.apply_preview.create_update_itineraries} />
          <AP label="Booking Pending (no itinerary)" v={data.apply_preview.booking_pending_no_itinerary} />
          <AP label="Historical voided preserved" v={data.apply_preview.historical_voided_preserved} />
          <AP label="Self-arranged exceptions" v={data.apply_preview.self_arranged_exceptions} />
          <AP label="Navan fare records" v={data.apply_preview.navan_fare_records} />
          <AP label="Master external fare records" v={data.apply_preview.master_fare_records} />
          <AP label="Current fares missing" v={data.apply_preview.current_fares_missing} />
          <AP label="Unresolved source" v={data.apply_preview.unresolved_source} />
          <AP label="Zero writes for Declined" v={data.apply_preview.zero_writes_for_declined} />
        </div>
        <div className="mt-3 text-xs text-[hsl(var(--muted-foreground))]">Sources: {data.sources?.join(" · ")}</div>
      </Card>
      )}

      <DetailDrawer row={sel} onClose={() => setSel(null)} />
      {showImport && (
        <ImportFlightSourcesModal
          eventId={eventId}
          onClose={() => setShowImport(false)}
          onAnalyzed={(res) => { setData(res); setShowImport(false); setFilter("All"); setQ(""); }}
        />
      )}
      {showApply && data.batch_signature && (
        <ApplyConfirmModal
          eventId={eventId}
          data={data}
          onClose={() => setShowApply(false)}
          onApplied={() => { setShowApply(false); navigate("/manager/travel"); }}
        />
      )}
    </div>
  );
}

const CONFIRM_TOKEN = "APPLY_PC26_FLIGHT_RECONCILIATION";

function ApplyConfirmModal({ eventId, data, onClose, onApplied }) {
  const [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [error, setError] = useState(null);
  const ap = data.apply_preview || {};

  const run = async (dry) => {
    setBusy(true); setError(null);
    try {
      const res = await api.flightReconciliationApply(eventId, {
        batch_signature: data.batch_signature,
        confirm_token: CONFIRM_TOKEN,
        dry_run: dry,
      });
      if (dry) {
        setDryResult(res);
      } else {
        // Applied: canonical state is now persisted. Clear the temporary uploaded
        // batch and return to Travel, which reads the same persisted canonical state.
        setApplyResult(res);
        setTimeout(() => onApplied?.(), 1600);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Apply request failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="apply-confirm-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-5 py-3.5">
          <h3 className="font-serif text-lg text-[hsl(var(--primary))] flex items-center gap-2"><ShieldCheck className="h-5 w-5" /> Review &amp; Apply analyzed batch</h3>
          <button onClick={onClose} data-testid="apply-modal-close" className="rounded-full p-1.5 hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] p-3 text-xs space-y-1">
            <div className="flex items-center justify-between"><span className="text-[hsl(var(--muted-foreground))]">Batch signature</span><span className="font-mono text-[hsl(var(--primary))]" data-testid="apply-batch-signature">{String(data.batch_signature).slice(0, 20)}…</span></div>
            {data.batch_expires_at && <div className="flex items-center justify-between"><span className="text-[hsl(var(--muted-foreground))]">Expires</span><span className="text-[hsl(var(--primary))]">{new Date(data.batch_expires_at).toLocaleString()}</span></div>}
            <div className="text-[hsl(var(--muted-foreground))]">Sources: {(data.sources || []).join(" · ")}</div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <AP label="Canonical itineraries" v={ap.create_update_itineraries} />
            <AP label="Booking Pending (no itin)" v={ap.booking_pending_no_itinerary} />
            <AP label="Navan fare records" v={ap.navan_fare_records} />
            <AP label="Master external fares" v={ap.master_fare_records} />
            <AP label="Self-arranged exceptions" v={ap.self_arranged_exceptions} />
            <AP label="Historical preserved" v={ap.historical_voided_preserved} />
          </div>

          {dryResult && (
            <div data-testid="apply-dryrun-result" className="rounded-xl border border-[hsl(var(--secondary)/0.4)] bg-[hsl(var(--secondary)/0.08)] p-3 text-xs">
              <div className="font-semibold text-[hsl(var(--secondary))] mb-1">Dry-run plan ({dryResult.integrity})</div>
              <div>Itineraries: {dryResult.planned?.itineraries} · Airfares: {dryResult.planned?.airfares} · Exceptions: {dryResult.planned?.exceptions} · Historical: {dryResult.planned?.historical}</div>
              <div className="mt-1 text-[hsl(var(--muted-foreground))]">Inserts: {dryResult.counts?.itin_inserted} new itineraries · {dryResult.counts?.itin_updated} updated · nothing written (dry-run)</div>
            </div>
          )}
          {applyResult && (
            <div data-testid="apply-applied-result" className="rounded-xl border border-[hsl(var(--mint))] bg-[hsl(var(--mint))] p-3 text-xs text-[hsl(var(--primary))]">
              <div className="font-semibold mb-1 flex items-center gap-1.5"><CircleCheck className="h-4 w-4" /> {applyResult.status || "Applied"} — canonical travel updated. Returning to Travel…</div>
              <div>Itineraries: {applyResult.planned?.itineraries} · Airfares: {applyResult.planned?.airfares} · Inserts: {applyResult.counts?.itin_inserted} · Updates: {applyResult.counts?.itin_updated}</div>
            </div>
          )}
          {error && <div data-testid="apply-error" className="rounded-lg bg-[hsl(0_70%_96%)] px-3 py-2 text-xs text-[hsl(0_60%_42%)]">{error}</div>}

          <label className="flex items-start gap-2 text-xs text-[hsl(var(--foreground))]">
            <input type="checkbox" data-testid="apply-confirm-checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} className="mt-0.5" />
            <span>I have reviewed the analyzed delta and confirm applying this exact uploaded batch to the canonical flight collections. This writes only canonical flight records (never attendees / RSVP / hotel).</span>
          </label>
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-[hsl(var(--border))] px-5 py-3.5">
          {applyResult ? (
            <button onClick={() => onApplied?.()} data-testid="apply-back-to-travel-btn" className="ml-auto inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white hover:opacity-90 transition-[opacity]">
              <ChevronRight className="h-4 w-4" /> Back to Travel
            </button>
          ) : (
            <>
              <button onClick={() => run(true)} disabled={busy} data-testid="apply-dryrun-btn" className="inline-flex items-center gap-2 rounded-full border border-[hsl(var(--border))] px-4 py-2 text-sm font-semibold text-[hsl(var(--primary))] hover:bg-[hsl(var(--muted))] disabled:opacity-50">
                {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Dry-run
              </button>
              <div className="flex items-center gap-2">
                <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button>
                <button onClick={() => run(false)} disabled={!confirmed || busy || !!applyResult} data-testid="apply-confirm-btn"
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white ${confirmed && !busy && !applyResult ? "bg-[hsl(var(--primary))] hover:opacity-90" : "bg-[hsl(var(--primary))] opacity-40 cursor-not-allowed"}`}>
                  <ShieldCheck className="h-4 w-4" /> Apply batch
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportFlightSourcesModal({ eventId, onClose, onAnalyzed }) {
  const [master, setMaster] = useState(null);
  const [manifests, setManifests] = useState([]); // File[]
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const addManifests = (fileList) => {
    const incoming = Array.from(fileList || []).filter((f) => f.name.toLowerCase().endsWith(".csv"));
    setManifests((prev) => {
      const names = new Set(prev.map((f) => f.name));
      const merged = [...prev, ...incoming.filter((f) => !names.has(f.name))];
      return merged.slice(0, 20);
    });
  };
  const removeManifest = (name) => setManifests((prev) => prev.filter((f) => f.name !== name));

  const canAnalyze = master && manifests.length > 0 && !busy;

  const runAnalyze = async () => {
    if (!canAnalyze) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append("travel_master", master);
      manifests.forEach((f) => fd.append("navan_files", f));
      const res = await api.flightReconciliationAnalyzeUpload(eventId, fd);
      onAnalyzed(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Analyze failed. Check the files and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" data-testid="import-flight-sources-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-[hsl(var(--border))] px-5 py-3.5">
          <h3 className="font-serif text-lg text-[hsl(var(--primary))] flex items-center gap-2"><Upload className="h-5 w-5" /> Import Flight Sources</h3>
          <button onClick={onClose} data-testid="import-modal-close" className="rounded-full p-1.5 hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-5 space-y-5">
          <p className="text-xs text-[hsl(var(--muted-foreground))]">Upload the current approved <strong>Travel Master</strong> workbook and the <strong>Navan manifests</strong>. Analyze is read-only — nothing is written until you explicitly Apply.</p>

          {/* Travel Master */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Travel Master · 1 workbook (.xlsx)</div>
            {master ? (
              <div className="flex items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] px-3 py-2 text-sm" data-testid="import-master-file">
                <span className="flex items-center gap-2 min-w-0"><FileSpreadsheet className="h-4 w-4 text-[hsl(var(--secondary))] shrink-0" /><span className="truncate">{master.name}</span></span>
                <button onClick={() => setMaster(null)} data-testid="import-master-remove" className="text-[hsl(0_60%_42%)] hover:opacity-80"><Trash2 className="h-4 w-4" /></button>
              </div>
            ) : (
              <label className="flex items-center gap-2 rounded-xl border border-dashed border-[hsl(var(--border))] px-3 py-3 text-sm text-[hsl(var(--muted-foreground))] cursor-pointer hover:bg-[hsl(var(--muted)/0.4)]" data-testid="import-master-drop">
                <Plus className="h-4 w-4" /> Choose Travel Master (.xlsx)
                <input type="file" accept=".xlsx" className="hidden" data-testid="import-master-input"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f && f.name.toLowerCase().endsWith(".xlsx")) setMaster(f); else if (f) setError("Travel Master must be a .xlsx file."); e.target.value = ""; }} />
              </label>
            )}
          </div>

          {/* Navan manifests */}
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">Navan Manifests · multiple (.csv) — up to 20</div>
            <div className="space-y-1.5">
              {manifests.map((f) => (
                <div key={f.name} className="flex items-center justify-between rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted)/0.4)] px-3 py-2 text-sm" data-testid="import-manifest-file">
                  <span className="flex items-center gap-2 min-w-0"><FileText className="h-4 w-4 text-[hsl(var(--secondary))] shrink-0" /><span className="truncate">{f.name}</span></span>
                  <button onClick={() => removeManifest(f.name)} data-testid="import-manifest-remove" className="text-[hsl(0_60%_42%)] hover:opacity-80"><Trash2 className="h-4 w-4" /></button>
                </div>
              ))}
            </div>
            <label className="mt-1.5 flex items-center gap-2 rounded-xl border border-dashed border-[hsl(var(--border))] px-3 py-3 text-sm text-[hsl(var(--muted-foreground))] cursor-pointer hover:bg-[hsl(var(--muted)/0.4)]" data-testid="import-manifest-drop">
              <Plus className="h-4 w-4" /> Add Navan manifest(s) (.csv)
              <input type="file" accept=".csv" multiple className="hidden" data-testid="import-manifest-input"
                onChange={(e) => { addManifests(e.target.files); e.target.value = ""; }} />
            </label>
          </div>

          {error && <div data-testid="import-error" className="rounded-lg bg-[hsl(0_70%_96%)] px-3 py-2 text-xs text-[hsl(0_60%_42%)]">{error}</div>}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-[hsl(var(--border))] px-5 py-3.5">
          <span className="text-xs text-[hsl(var(--muted-foreground))]">{manifests.length} manifest{manifests.length === 1 ? "" : "s"} · {master ? "1 master" : "no master"}</span>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="rounded-full px-4 py-2 text-sm font-medium text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))]">Cancel</button>
            <button onClick={runAnalyze} disabled={!canAnalyze} data-testid="import-analyze-btn"
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white ${canAnalyze ? "bg-[hsl(var(--primary))] hover:opacity-90" : "bg-[hsl(var(--primary))] opacity-40 cursor-not-allowed"}`}>
              {busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Plane className="h-4 w-4" />} {busy ? "Analyzing…" : "Analyze"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SrcRow({ label, v }) {
  return <div className="flex items-center justify-between"><span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-semibold text-[hsl(var(--primary))]">{v}</span></div>;
}
function AP({ label, v }) {
  return <div className="flex items-center justify-between border-b border-[hsl(var(--border))] py-1"><span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-semibold text-[hsl(var(--primary))]">{v}</span></div>;
}

function DetailDrawer({ row, onClose }) {
  if (!row) return null;
  const m = row.detail?.master;
  const navs = row.detail?.navan || [];
  return (
    <Sheet open={!!row} onOpenChange={(o) => !o && onClose()}>
      <SheetContent data-testid="flight-recon-drawer" className="w-full sm:max-w-xl overflow-y-auto">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-serif text-xl text-[hsl(var(--primary))]">{row.attendee}</div>
            <div className="text-sm text-[hsl(var(--muted-foreground))]">{row.email} · <span className="capitalize">{row.rsvp}</span></div>
          </div>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-[hsl(var(--muted))]"><X className="h-4 w-4" /></button>
        </div>

        <div className="mt-4"><Badge text={row.travel_status} cls={STATUS_STYLES[row.travel_status]} /></div>

        {row.note && <div data-testid="drawer-note" className="mt-3 rounded-xl bg-[hsl(var(--pc-gold)/0.1)] p-3 text-sm text-[hsl(38_45%_30%)]">{row.note}</div>}

        {/* Canonical itinerary */}
        <Section title="Canonical Itinerary">
          <KV label="Selected source" v={row.canonical_source} />
          <KV label="Managed / self-arranged" v={row.managed_indicator} />
          <KV label="Arrival (SJD)" v={row.arr_flight ? `${dstr(row.arr_date)} ${row.arr_time ? String(row.arr_time).slice(0,5) : ""} · ${row.arr_flight}${row.arr_origin ? " · from " + row.arr_origin : ""}` : "—"} />
          <KV label="Departure (SJD)" v={row.dep_flight ? `${dstr(row.dep_date)} · ${row.dep_flight}${row.dep_final ? " · to " + row.dep_final : ""}` : (row.travel_status === "Complete · Return self-arranged" ? "Self-arranged (attendee-managed)" : "—")} />
          <KV label="Booking reference" v={row.booking_ref || "—"} />
          <KV label="Source file" v={row.source_file || "—"} />
        </Section>

        {/* Airfare */}
        <Section title="Airfare (Manager-only)">
          <KV label="Current canonical fare" v={money(row.airfare, row.currency)} />
          <KV label="Currency" v={row.currency || "Unknown"} />
          <KV label="Fare source" v={row.fare_source || "—"} />
          <KV label="Fare status" v={row.fare_status} />
        </Section>

        {/* Source comparison */}
        <Section title="Source Comparison">
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase mb-1">Master</div>
          {m ? (
            <div className="text-sm space-y-0.5">
              <div>Source status: <b>{m.flight_source_status || "—"}</b></div>
              <div>Arr: {m.arr.airport || "—"} {dstr(m.arr.date)} {m.arr.flight || ""}</div>
              <div>Dep: {m.dep.airport || "—"} {dstr(m.dep.date)} {m.dep.flight || ""}</div>
              <div>Airfare: {m.airfare == null ? "—" : m.airfare}</div>
            </div>
          ) : <div className="text-sm text-[hsl(var(--muted-foreground))]">No Master row.</div>}
          <div className="text-xs font-semibold text-[hsl(var(--muted-foreground))] uppercase mt-3 mb-1">Navan</div>
          {navs.length ? navs.map((n, i) => (
            <div key={i} className="text-sm space-y-0.5 border-t border-[hsl(var(--border))] pt-1.5 mt-1.5">
              <div className="flex items-center gap-2">{n.manifest} · {n.booking_status} / {n.flight_booking_status}
                {n.voided && <Badge text="Superseded / Historical" cls="bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))]" />}</div>
              <div>Outbound: {n.ob.origin || "?"} → {n.ob.conn ? n.ob.conn + " → " : ""}{n.ob.arr_airport || "?"} {dstr(n.ob.date)} {n.ob.time ? String(n.ob.time).slice(0,5) : ""} {n.ob.flight || ""}</div>
              <div>Inbound: {n.ib.dep_airport ? `${n.ib.dep_airport} ${dstr(n.ib.date)} ${n.ib.flight || ""}` : "—"}</div>
              <div>Cost: {n.cost == null ? "—" : `${n.cost} USD`}{n.voided ? " (historical)" : ""}</div>
            </div>
          )) : <div className="text-sm text-[hsl(var(--muted-foreground))]">No Navan row.</div>}
          <div className="text-xs font-semibold text-[hsl(var(--primary))] uppercase mt-3 mb-1">Canonical selected</div>
          <div className="text-sm">{row.canonical_source} — {row.travel_status}</div>
        </Section>

        {row.canonical_source === "Unknown" && (
          <button data-testid="resolve-source-btn" disabled className="mt-4 w-full rounded-full bg-[hsl(var(--muted))] px-4 py-2 text-sm font-semibold text-[hsl(var(--muted-foreground))] cursor-not-allowed">Resolve Source (disabled — no writes in Phase 2)</button>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-5">
      <div className="text-sm font-semibold text-[hsl(var(--primary))] mb-2 flex items-center gap-1.5"><FileSpreadsheet className="h-4 w-4" /> {title}</div>
      <div className="rounded-xl border border-[hsl(var(--border))] p-3">{children}</div>
    </div>
  );
}
function KV({ label, v }) {
  return <div className="flex items-center justify-between gap-3 py-0.5 text-sm"><span className="text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-medium text-right text-[hsl(var(--foreground))]">{v}</span></div>;
}
